<template>
  <div class="animated fadeIn">
    <b-col xl="4" md="6" offset-xl="4" offset-md="3">
      <b-card bg-variant="light" header-tag="h5" :header="$t('ACCOUNT.ADD_NEW')" header-class="card-header-center card-header-primary card-header-uppercase" footer-class="card-footer-between">
        <b-form-group>
          <label for="name">{{ $t('COMMON.NAME') }}</label>
          <span class="required">*</span>
          <input type="text" class="form-control"
                 :class="{'is-invalid': $v.account.name.$error, 'is-valid': !$v.account.name.$invalid}"
                 :placeholder="$t('ACCOUNT.NAME')"
                 v-model="account.name"
                 @blur="$v.account.name.$touch()">
          <b-form-invalid-feedback v-if="!$v.account.name.required">{{ $t('ACCOUNT.VALID_BLANK') }}</b-form-invalid-feedback>
          <b-form-invalid-feedback v-if="!$v.account.name.isUnique">{{ $t('ACCOUNT.VALID_EXIST') }}</b-form-invalid-feedback>
          <b-form-invalid-feedback v-if="!$v.account.name.minLen">{{ $t('ACCOUNT.VALID_MIN') }}</b-form-invalid-feedback>
        </b-form-group>
        <b-form-group>
          <label for="account_type">{{ $t('ACCOUNT.ACCOUNT_TYPE') }}</label>
          <span class="required">*</span>
          <b-form-select class="form-control" :plain="true"
                          :class="{'is-invalid': $v.account.account_type.$error, 'is-valid': !$v.account.account_type.$invalid}"
                          :options="account_types"
                          v-model="account.account_type">
          </b-form-select>
          <b-form-invalid-feedback v-if="!$v.account.account_type.required">{{ $t('ACCOUNT.VALID_ACCOUNT_TYPE') }}</b-form-invalid-feedback>
        </b-form-group>
        <b-form-group>
          <label for="ocr_engine">{{ $t('ACCOUNT.OCR_ENGINE') }}</label>
          <span class="required">*</span>
          <b-form-select class="form-control" :plain="true"
                          :class="{'is-invalid': $v.account.ocr_engine.$error, 'is-valid': !$v.account.ocr_engine.$invalid}"
                          :options="ocr_engines"
                          v-model="account.ocr_engine">
          </b-form-select>
          <b-form-invalid-feedback v-if="!$v.account.ocr_engine.required">{{ $t('ACCOUNT.VALID_OCR_ENGINE') }}</b-form-invalid-feedback>
        </b-form-group> 
        <b-form-group>
          <label for="expires_at">{{ $t('ACCOUNT.END_DATE') }}</label>
          <span class="required">*</span>
          <input class="form-control" type="date" :min="currDate"
                  value="account.expires_at" v-model="account.expires_at"
                  :class="{'is-invalid': $v.account.expires_at.$error, 'is-valid': !$v.account.expires_at.$invalid}" />
          <b-form-invalid-feedback v-if="!$v.account.expires_at.required || !$v.account.expires_at.isValid">{{ $t('ACCOUNT.VALID_END_DATE') }}</b-form-invalid-feedback>
        </b-form-group>
        <b-form-group>
          <label for="process_categories">{{ $t('ACCOUNT.PROCESS_TYPE') }}</label>
          <span class="required">*</span>
          <multiselect  :options="processCategories" v-model="account.process_categories" 
                        :multiple="true" :max-height="200">
          </multiselect>
          <input class="form-control" hidden :class="{'is-invalid': $v.account.process_categories.$error, 'is-valid': !$v.account.process_categories.$invalid}">
          <b-form-invalid-feedback v-if="!$v.account.process_categories.isValid">{{ $t('ACCOUNT.VALID_PROCESS_TYPE') }}</b-form-invalid-feedback>
        </b-form-group>
        <b-form-group>
          <label for="process_categories">Salesforce System</label>
          <span class="required">*</span>
          <multiselect  :options="salesforceExternalSystems" v-model="account.salesforce_system"
                        :multiple="false" :max-height="200" :custom-label="dropDownLabel">
          </multiselect>
          <input class="form-control" hidden :class="{'is-invalid': $v.account.process_categories.$error, 'is-valid': !$v.account.process_categories.$invalid}">
          <b-form-invalid-feedback v-if="!$v.account.process_categories.isValid">{{ $t('ACCOUNT.VALID_PROCESS_TYPE') }}</b-form-invalid-feedback>
        </b-form-group>

        <b-form-group>
          <label for="process_categories">Mulesoft System</label>
          <span class="required">*</span>
          <multiselect  :options="mulesoftExternalSystems" v-model="account.mulesoft_system"
                        :multiple="false" :max-height="200" :custom-label="dropDownLabel">
          </multiselect>
          <input class="form-control" hidden :class="{'is-invalid': $v.account.process_categories.$error, 'is-valid': !$v.account.process_categories.$invalid}">
          <b-form-invalid-feedback v-if="!$v.account.process_categories.isValid">{{ $t('ACCOUNT.VALID_PROCESS_TYPE') }}</b-form-invalid-feedback>
        </b-form-group>

        <b-form-group>
          <label for="integration_emails">{{ $t('COMMON.INTEGRATION_EMAILS') }}</label>
          <textarea class="form-control" v-model="integration_emails"
                    :placeholder="$t('COMMON.ENTER_MULTIPLE_EMAILS')"
                    :class="{'is-invalid': $v.account.integration_emails.$error, 'is-valid': !$v.account.integration_emails.$invalid}" />
          <b-form-invalid-feedback v-if="$v.account.integration_emails.$invalid">{{ $t('ACCOUNT.VALID_EMAIL') }}</b-form-invalid-feedback>
        </b-form-group>
        <b-form-group>
          <label for="notification_emails">{{ $t('COMMON.NOTIFICATION_EMAILS') }}</label>
          <textarea class="form-control" v-model="notification_emails"
                    :placeholder="$t('COMMON.ENTER_MULTIPLE_EMAILS')"
                    :class="{'is-invalid': $v.account.notification_emails.$error, 'is-valid': !$v.account.notification_emails.$invalid}"
                 />
          <b-form-invalid-feedback v-if="$v.account.notification_emails.$invalid">{{ $t('ACCOUNT.VALID_EMAIL') }}</b-form-invalid-feedback>
        </b-form-group>
        <b-form-group>
          <label for="notification_emails">{{ $t('ACCOUNT.ACCOUNT_LOGO') }}</label> <br/>
          <input id="fileUpload" type="file" ref="file" accept="image/png, image/jpeg" required=true />
          <span @click="removeFile()"> <i class="fa fa-times"></i></span>
        </b-form-group>
        <b-form-group>
          <c-switch type="3d" variant="success" @change="changeAutoExportStatus($event)" /> Auto Export to Salesforce
        </b-form-group>
        <b-form-group v-if="account.salesforce_auto_export">
          <div class="range-slider" >
            <input v-model="account.confidenceScore" min="0" max="100" step="1" type="range" />
            <span> Select {{ $t('FORMS.CONF_RANGE') }} : {{account.confidenceScore}} - 100</span>
          </div>
          <div class="note">Note: Select Confidence Range to auto export Data to Sales force if confidence range meets the criteria. Default Range is 90-100</div>
        </b-form-group>
        <div slot="footer">
          <b-button type="submit" class="padding-x-2" size="sm" variant="secondary" @click="createAccount()"><i class="fa fa-dot-circle-o"></i> {{ $t('COMMON.SUBMIT') }} </b-button>
          <b-button type="reset" class="padding-x-2" size="sm" variant="gray" @click="resetForm()"><i class="fa fa-ban"></i> {{ $t('COMMON.RESET') }} </b-button>
        </div>
      </b-card>
    </b-col>
    <b-modal :title="$t('ACCOUNT.ERRORS')" centered @hidden="hideErrorsModal" ref="errorsModal" hide-footer>
        <b-container fluid>
          <div>
            <b-row class="mb-1 text-center">
              <b-col cols="6"><strong>{{ $t('ACCOUNT.EMAIL') }}</strong></b-col>
              <b-col cols="6"><strong>{{ $t('ACCOUNT.REASON') }}</strong></b-col>
            </b-row>
            <b-row class="mb-1 text-center" v-for="(error, index) in errors" :key="error.email + index">
              <b-col cols="6" class="warning">{{ error.email }} </b-col>
              <b-col cols="6" class="warning">{{ $t(`ACCOUNT.${error.reason}`) }} </b-col>
            </b-row>
          </div>
        </b-container>
      </b-modal>
    <b-modal :title="$t('COMMON.DELETE_CONFIRM')" ref="logoDeleteModal" id="modal-center" centered :cancel-title="$t('COMMON.NO')" @ok="deleteFile" :ok-title="$t('COMMON.YES')">
      <p class="my-4"> {{ $t('ACCOUNT.DELETE_CONTENT') }}</p>
    </b-modal>

  </div>
</template>
<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import router from '../../router'
import _ from 'lodash'
import moment from 'moment'
import { required, minLength, email } from 'vuelidate/lib/validators'
import Multiselect from 'vue-multiselect'
import cSwitch from '@/components/Switch'

export default {
  components: { Multiselect, 'c-switch': cSwitch },
  data () {
    return {
      account: {
        name: null,
        account_type: null,
        ocr_engine: null,
        expires_at: null,
        integration_emails: [],
        notification_emails: [],
        process_categories: [],
        salesforce_auto_export: false,
        confidenceScore: 90,
        salesforce_system: null,
        mulesoft_system: null
      },
      integration_emails: '',
      notification_emails: '',
      account_types: [
        { text: this.$t('PROFILE.ROLES.TYPE_SELECT'), value: null },
        { text: this.$t('PROFILE.ROLES.TRIAL'), value: 'trial' },
        { text: this.$t('PROFILE.ROLES.SUBS_ACC'), value: 'subscribed' }
      ],
      ocr_engines: [
        { text: 'Select Ocr Engine', value: null },
        { text: 'Google Vision', value: 1 },
        { text: 'AWS Rekognition', value: 2 },
        { text: 'AWS Textract + AWS Rekognition', value: 3 },
        { text: 'AWS Textract', value: 4 }
      ],
      currDate: moment().format('YYYY-MM-DD').toString(),
      errors: [],
      processCategories: [],
      salesforceExternalSystems: [],
      mulesoftExternalSystems: []
    }
  },
  computed: {
    ...mapGetters(['isSuperAdmin', 'idToken'])
  },
  watch: {
    'account.account_type': function () {
      if (this.account.account_type === 'trial') {
        this.account.expires_at = moment().add(2, 'weeks').format('YYYY-MM-DD').toString()
      } else if (this.account.account_type === 'subscribed') {
        this.account.expires_at = moment().add(1, 'years').format('YYYY-MM-DD').toString()
      } else {
        this.account.expires_at = null
      }
    },
    'integration_emails': function () {
      this.account.integration_emails = this.integration_emails.split(',')
    },
    'notification_emails': function () {
      this.account.notification_emails = this.notification_emails.split(',')
    }
  },
  beforeRouteEnter (to, from, next) {
    next(vm => {
      if (!vm.isSuperAdmin) {
        router.replace('/Not-Found-404')
      } else {
        Vue.axios.get('/settings/process_types/categories', { headers: { Authorization: `Bearer ${vm.idToken}` }
        })
          .then(res => {
            vm.processCategories = res.data.categories
            vm.account.process_categories = vm.processCategories
            vm.salesforceExternalSystems = res.data.salesforce_systems.map(es => { return { text: es.title, value: es.id } })
            vm.account.salesforce_system = vm.salesforceExternalSystems[0]
            vm.mulesoftExternalSystems = res.data.mulesoft_systems.map(es => { return { text: es.title, value: es.id } })
            vm.account.mulesoft_system = vm.mulesoftExternalSystems[0]
          })
          .catch(error => {
            console.log(error)
            if (error.status === 401) {
              Vue.toasted.show(vm.$t('COMMON.MSG_EXPIRED'), { icon: 'chain-broken', type: 'error' })
              console.log(this.$t('COMMON.MSG_EXPIRED'))
            } else {
              Vue.toasted.show(vm.$t('COMMON.MSG_RETRY'), { icon: 'chain-broken', type: 'error' })
              next(false)
            }
          })
      }
    })
  },
  methods: {
    dropDownLabel ({text}) {
      return `${text}`
    },
    createAccount () {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        let vm = this
        let accountFormData = new window.FormData()
        accountFormData.append('name', this.account.name)
        accountFormData.append('account_type', this.account.account_type)
        accountFormData.append('ocr_engine', this.account.ocr_engine)
        accountFormData.append('expires_at', this.account.expires_at)
        accountFormData.append('integration_emails', JSON.stringify(this.account.integration_emails))
        accountFormData.append('notification_emails', JSON.stringify(this.account.notification_emails))
        accountFormData.append('process_categories', JSON.stringify(this.account.process_categories))
        accountFormData.append('salesforce_auto_export', this.account.salesforce_auto_export)
        accountFormData.append('salesforce_system', this.account.salesforce_system.value)
        accountFormData.append('mulesoft_system', this.account.mulesoft_system.value)
        if (this.account.salesforce_auto_export) {
          accountFormData.append('confidence_score', this.account.confidenceScore)
        }
        if (this.$refs.file.files.length === 1) {
          accountFormData.append('image', this.$refs.file.files[0])
        }
        Vue.axios.post('/admin/accounts', accountFormData, { headers: { Authorization: `Bearer ${this.idToken}` } })
          .then(res => {
            if (res.data.success) {
              vm.$store.state.accounts.push(res.data.account)
              router.push('/super_admin/accounts')
              Vue.toasted.show(this.$t('COMMON.MSG_ACC_CREATE'))
            } else {
              if (res.data.msg) {
                Vue.toasted.show(this.$t('COMMON.MSG_SIGNUP_FAIL'), { icon: 'chain-broken', type: 'error' })
              } else {
                this.errors = res.data.errors
                this.$refs.errorsModal.show()
              }
            }
          })
          .catch(error => {
            Vue.toasted.show(this.$t('COMMON.MSG_SIGNUP_FAIL'), { icon: 'chain-broken', type: 'error' })
            console.log(error)
          })
      }
    },
    changeAutoExportStatus: function (event) {
      this.account.salesforce_auto_export = event
    },
    resetForm () {
      this.account = {
        name: null,
        account_type: null,
        ocr_engine: null,
        expires_at: null,
        integration_emails: [],
        notification_emails: [],
        process_categories: this.processCategories,
        salesforce_auto_export: false
      }
      this.integration_emails = ''
      this.notification_emails = ''
      this.$v.account.$reset()
      this.$refs.file.value = null
    },
    removeFile () {
      if (this.$refs.file.files.length === 1) {
        this.$refs.logoDeleteModal.show()
      }
    },
    deleteFile () {
      this.$refs.file.value = null
      this.$refs.logoDeleteModal.hide()
    },
    hideErrorsModal () {
      this.errors = []
      this.$refs.errorsModal.hide()
    }
  },
  validations: {
    account: {
      name: {
        required,
        minLen: minLength(5),
        isUnique (value, vm) {
          if (value === '') return true
          return new Promise((resolve, reject) => {
            setTimeout(() => {
              resolve(!_.some(this.$store.state.accounts, {'name': value}))
            }, 350 + Math.random() * 300)
          })
        }
      },
      account_type: {
        required
      },
      ocr_engine: {
        required
      },
      expires_at: {
        required,
        isValid (value, vm) {
          return moment(value) >= moment(vm.currDate)
        }
      },
      integration_emails: {
        $each: {
          email
        }
      },
      notification_emails: {
        $each: {
          email
        }
      },
      process_categories: {
        isValid (value, vm) {
          return value.length > 0
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .warning,
  .required {
    color: red;
  }
  .range-slider {
    max-width: 75%;
    margin: auto;
    text-align: center;
    position: relative;
    height: 6em;
    font-size: 0.8rem;
    padding: 5px;
  }
  .range-slider input[type=range] {
    position: absolute;
    left: 0;
    bottom: 0;
  }
  input[type=range] {
    -webkit-appearance: none;
    width: 100%;
  }

  input[type=range]:focus {
    outline: none;
  }

  input[type=range]:focus::-webkit-slider-runnable-track {
    background: green;
  }

  input[type=range]:focus::-ms-fill-lower {
    background: green;
  }

  input[type=range]:focus::-ms-fill-upper {
    background: green;
  }

  input[type=range]::-webkit-slider-runnable-track {
    width: 100%;
    height: 5px;
    cursor: pointer;
    animate: 0.2s;
    background: green;
    border-radius: 1px;
    box-shadow: none;
    border: 0;
    margin-bottom:0px;
    margin-top:-70px;
  }

  input[type=range]::-webkit-slider-thumb {
    z-index: 2;
    position: relative;
    box-shadow: 0px 0px 0px #000;
    border: 1px solid #2497e3;
    height: 18px;
    width: 18px;
    border-radius: 25px;
    background: #10ac3e;
    cursor: pointer;
    -webkit-appearance: none;
    margin-top: -8px;
  }
  .note{
    text-align: center;
    color: red;
  }
</style>



// WEBPACK FOOTER //
// src/views/superAdmin/newAccount.vue